/* ==========================================================================
   Pacem product page

   Three small jobs, all of which have to survive Dawn re-rendering the whole
   info column on every variant change (product-info.js swaps this subtree's
   innerHTML), so everything is delegated from document rather than bound to
   elements directly, and state that must outlive a re-render lives up here.
   ========================================================================== */
(function () {
  'use strict';

  /* Products where the shopper has actually picked a size. Dawn re-renders from
     the server, and the server re-renders the "Select Size" placeholder whenever
     it can't resolve an explicit variant — which would throw away a choice the
     shopper had already made. Remembering it here, outside the swapped subtree,
     keeps the selection sticky. Keyed by product id. */
  var sizeChosen = new Set();

  function productInfoOf(el) {
    return el && el.closest ? el.closest('product-info') : null;
  }

  function productIdOf(el) {
    var info = productInfoOf(el);
    return info ? info.dataset.productId : null;
  }

  /* ---- Add to bag: disable until a size is chosen -------------------------
     The server always renders around selected_or_first_available_variant, so
     while the placeholder is showing, the form's hidden variant id points at a
     size nobody picked. Submitting then would silently add the wrong one. */
  function syncSubmitState(scope) {
    var root = scope || document;

    root.querySelectorAll('product-info').forEach(function (info) {
      var pendingSelect = Array.prototype.find.call(
        info.querySelectorAll('select[data-ppdp-size]'),
        function (select) {
          return select.value === '';
        }
      );
      var placeholderActive = Boolean(pendingSelect);

      /* The prompt may name the option ("Select a [option]"), so a Style
         dropdown asks for a style rather than a size. */
      var pendingName = '';
      if (pendingSelect) {
        var nameEl = pendingSelect.closest('.ppdp-option');
        nameEl = nameEl && nameEl.querySelector('.ppdp-optlabel > span');
        pendingName = nameEl ? nameEl.textContent.trim().toLowerCase() : '';
      }

      info.querySelectorAll('.ppdp-buy .product-form__submit').forEach(function (button) {
        // Never re-enable a button the server disabled (sold out, unavailable).
        if (button.dataset.ppdpServerDisabled === 'true') return;

        if (button.dataset.ppdpServerDisabled === undefined) {
          button.dataset.ppdpServerDisabled = String(button.hasAttribute('disabled'));
          if (button.hasAttribute('disabled')) return;
        }

        var label = button.querySelector('.ppdp-buy__label');
        var priceParts = button.querySelectorAll('.ppdp-buy__divider, .ppdp-buy__price');

        /* Write only on an actual change. Assigning textContent replaces the
           text node, which the MutationObserver below counts as an added node
           — so an unconditional write here re-triggers refresh(), which writes
           again, and the page spins. Guarding the writes breaks that cycle. */
        if (placeholderActive) {
          if (!button.hasAttribute('disabled')) button.setAttribute('disabled', 'disabled');
          if (label && !label.dataset.ppdpDefault) label.dataset.ppdpDefault = label.textContent;
          var prompt = (button.dataset.ppdpSelectPrompt || 'Select a size').replace('[option]', pendingName || 'size');
          if (label && label.textContent !== prompt) label.textContent = prompt;
          priceParts.forEach(function (n) {
            if (!n.hidden) n.hidden = true;
          });
        } else {
          if (button.hasAttribute('disabled')) button.removeAttribute('disabled');
          if (label && label.dataset.ppdpDefault && label.textContent !== label.dataset.ppdpDefault) {
            label.textContent = label.dataset.ppdpDefault;
          }
          priceParts.forEach(function (n) {
            if (n.hidden) n.hidden = false;
          });
        }
      });
    });
  }

  /* ---- Keep a chosen size selected across re-renders ---------------------- */
  function restoreChosenSize(scope) {
    var root = scope || document;

    root.querySelectorAll('select[data-ppdp-size]').forEach(function (select) {
      var pid = productIdOf(select);
      if (!pid || !sizeChosen.has(pid)) return;

      var placeholder = select.querySelector('option[value=""]');
      if (placeholder && select.value === '') {
        // The shopper has chosen before, so the placeholder is stale — drop it
        // and fall back to whatever the server marked selected.
        placeholder.remove();
        var marked = select.querySelector('option[selected]');
        if (marked) select.value = marked.value;
      }
    });
  }

  /* ---- Wishlist slot: keep data-variant-id current ------------------------
     The slot is inert until an app mounts on it, but the app will read this
     attribute, so it has to track the selected variant. */
  function syncWishlistVariant(scope) {
    var root = scope || document;

    root.querySelectorAll('product-info').forEach(function (info) {
      var variantInput = info.querySelector('input[name="id"], .product-variant-id');
      if (!variantInput) return;

      info.querySelectorAll('[data-pacem-wishlist]').forEach(function (button) {
        button.dataset.variantId = variantInput.value;
      });
    });
  }

  /* ---- Made to order / Ready to ship cards: follow the selected variant ----
     Liquid sorts each variant by its inventory settings (pdp-made-to-order),
     decides the opening variant's card and lists each card's variant ids in
     data-mto-variants; product-info.js does not re-render these cards on a
     variant change, so the switching is done here. */
  function syncMadeToOrder(scope) {
    var root = scope || document;

    root.querySelectorAll('product-info').forEach(function (info) {
      var variantInput = info.querySelector('input[name="id"], .product-variant-id');
      if (!variantInput) return;

      var selected = String(variantInput.value || '');

      info.querySelectorAll('.ppdp-mto[data-mto-variants]').forEach(function (card) {
        var ids = card.dataset.mtoVariants.split(',');
        card.hidden = ids.indexOf(selected) === -1;
      });
    });
  }

  function refresh(scope) {
    restoreChosenSize(scope);
    syncSubmitState(scope);
    syncWishlistVariant(scope);
    syncMadeToOrder(scope);
  }

  /* ---- Info-row accordions: animate the close ------------------------------
     Opening animates by itself — the panel is in the DOM and visible the moment
     `open` is set, so the grid row eases from 0fr to 1fr. Closing does not: the
     browser hides the content as soon as `open` is removed, leaving nothing to
     ease. So the close is intercepted, `data-closing` runs the same transition
     in reverse, and `open` is dropped only once it has finished. */
  var CLOSE_MS = 320;

  document.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('.ppdp-row--accordion > .ppdp-row__summary');
    if (!summary) return;

    var row = summary.parentElement;
    if (!row.open || row.dataset.closing) return;

    event.preventDefault();
    row.dataset.closing = 'true';

    window.setTimeout(function () {
      delete row.dataset.closing;
      row.open = false;
    }, CLOSE_MS);
  });

  /* ---- Info-row accordions: one open at a time ----------------------------
     Opening a row closes any other open row in the same info column, through
     the same animated close as a click on its summary (client, 2026-09-24). */
  document.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('.ppdp-row--accordion > .ppdp-row__summary');
    if (!summary) return;

    var row = summary.parentElement;
    if (row.open) return;

    var scope = productInfoOf(row) || document;
    scope.querySelectorAll('.ppdp-row--accordion[open]').forEach(function (other) {
      if (other === row || other.dataset.closing) return;
      other.dataset.closing = 'true';
      window.setTimeout(function () {
        delete other.dataset.closing;
        other.open = false;
      }, CLOSE_MS);
    });
  });

  /* ---- Add to bag: follow the selected variant -----------------------------
     product-info.js does not re-render the buy button. On a variant change it
     only toggles it, writing Dawn's "Add to cart" string over the label, and
     the price inside the button kept the opening variant's (Single still read
     $3,500). The fetched section HTML carries the right label and price, so
     copy them across once Dawn has finished. */
  function syncBuyButton(event) {
    var data = event && event.data;
    if (!data || !data.html || !data.sectionId) return;

    var fresh = data.html.getElementById('ProductSubmitButton-' + data.sectionId);
    if (!fresh) return;

    document.querySelectorAll('#ProductSubmitButton-' + data.sectionId).forEach(function (button) {
      ['.ppdp-buy__label', '.ppdp-buy__price'].forEach(function (selector) {
        var from = fresh.querySelector(selector);
        var to = button.querySelector(selector);
        if (!from || !to) return;
        if (selector === '.ppdp-buy__label') to.dataset.ppdpDefault = from.textContent;
        if (to.textContent !== from.textContent) to.textContent = from.textContent;
      });
    });

    // Re-apply the placeholder prompt if a size still has to be picked.
    syncSubmitState();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, syncBuyButton);
  }

  /* ---- Wiring ------------------------------------------------------------ */

  document.addEventListener('change', function (event) {
    var select = event.target.closest && event.target.closest('select[data-ppdp-size]');
    if (select) {
      if (select.value !== '') {
        var pid = productIdOf(select);
        if (pid) sizeChosen.add(pid);

        // Once a real size is picked the placeholder has done its job. Removing
        // it here also keeps VariantSelects.updateSelectionMetadata() happy: it
        // looks for the one option carrying the `selected` attribute, and the
        // placeholder is the one that has it on first interaction.
        var placeholder = select.querySelector('option[value=""]');
        if (placeholder) {
          placeholder.removeAttribute('selected');
          var chosen = select.selectedOptions[0];
          if (chosen) chosen.setAttribute('selected', 'selected');
          placeholder.remove();
        }
      }
      syncSubmitState();
    }
  });

  /* ---- Drawers: close on a click outside ---------------------------------
     popover="auto" is supposed to light-dismiss on its own, and Escape and the
     close button both work. This is a belt-and-braces fallback: it costs a few
     lines, it is a no-op when the native behaviour fires first (hidePopover on
     an already-closing popover does nothing), and it means a click on the
     dimmed backdrop always closes the panel even where light dismiss doesn't.

     Bound on pointerdown to match the native timing, and it ignores anything
     inside a drawer or on a popover trigger so it can't fight either. */
  document.addEventListener('pointerdown', function (event) {
    var open = document.querySelectorAll('.ppdp-drawer:popover-open');
    if (!open.length) return;

    var target = event.target;
    if (!target || !target.closest) return;
    if (target.closest('[popovertarget]')) return;

    open.forEach(function (drawer) {
      if (drawer.contains(target)) return;
      try {
        drawer.hidePopover();
      } catch (e) {
        /* already closing */
      }
    });
  });

  /* product-info.js replaces the info column's contents on every variant
     change. Watch for that and re-apply, rather than trying to hook its
     internals. */
  var observer = new MutationObserver(function (mutations) {
    var touched = false;
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        touched = true;
        break;
      }
    }
    if (touched) refresh();
  });

  function start() {
    document.querySelectorAll('product-info').forEach(function (info) {
      observer.observe(info, { childList: true, subtree: true });
    });
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
